import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowUpRight, Search, Check, ChevronDown, MapPin, Clock, Shield } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { hreflangLinks, localizedPath, FR_CITY_SLUGS, SITE_BASE_URL } from "@/i18n/paths";
import { cities, getCityBySlug } from "@/data/cities";
import { services } from "@/data/services";
import { slugifyKeyword } from "@/data/seoKeywords";
import { sharedCanadianKeywords, topCanadianKeywords } from "@/data/sharedKeywords";
import { FadeIn, MaskedReveal } from "@/components/Reveal";
import { JsonLd, citySchema, breadcrumbSchema } from "@/components/JsonLd";
import { TESTIDS } from "@/constants/testIds";

export const CitiesList = () => {
  const { t } = useLang();
  const [q, setQ] = React.useState("");
  const filtered = cities.filter(
    (c) => c.name.toLowerCase().includes(q.toLowerCase()) || c.region.toLowerCase().includes(q.toLowerCase())
  );
  const grouped = filtered.reduce((acc, c) => {
    (acc[c.region] = acc[c.region] || []).push(c);
    return acc;
  }, {});

  return (
    <>
      <Helmet>
        <title>Quebec Cities — Security Guard Services Coverage | Quebec Security Services</title>
        <meta name="description" content="Licensed security guard services in 50+ Quebec cities — Montreal, Quebec City, Laval, Gatineau, Sherbrooke, Val-d'Or, Sept-Iles, Fermont, Rouyn-Noranda and every incorporated municipality. Bilingual, 24/7 deployment." />
        <meta name="keywords" content="security guard Quebec cities, agent de sécurité villes Québec, security company Montreal Laval Quebec City, mining security Val-d'Or Fermont, security services Gatineau Sherbrooke" />
      </Helmet>

      <section className="px-6 md:px-10 pt-24 md:pt-32 pb-16 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-6">
          <span className="w-8 h-px bg-amber-500" />
          {t("nav.cities")} · {cities.length}
        </div>
        <h1 className="font-display uppercase font-black text-5xl md:text-7xl lg:text-8xl tracking-tight leading-[0.9] text-white max-w-5xl">
          <MaskedReveal>
            <span>{t("cities.list_title")}</span>
          </MaskedReveal>
        </h1>
        <FadeIn delay={0.2}>
          <p className="mt-8 text-zinc-400 text-lg max-w-2xl">{t("cities.list_sub")}</p>
        </FadeIn>

        <FadeIn delay={0.25}>
          <div className="mt-12 relative max-w-md">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid={TESTIDS.cities.filter}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by city or region…"
              className="w-full bg-transparent border border-white/15 focus:border-amber-500 h-12 pl-11 pr-4 text-sm text-white placeholder:text-zinc-600 focus:outline-none transition-colors"
            />
          </div>
        </FadeIn>
      </section>

      <section className="px-6 md:px-10 pb-24 max-w-[1600px] mx-auto">
        {Object.entries(grouped).map(([region, list]) => (
          <div key={region} className="mb-16">
            <div className="flex items-baseline justify-between border-b border-white/10 pb-4 mb-6">
              <h2 className="font-display uppercase text-xl md:text-2xl tracking-tight text-white">{region}</h2>
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                {list.length} {list.length === 1 ? "city" : "cities"}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {list.map((c) => (
                <Link
                  key={c.slug}
                  to={`/cities/${c.slug}`}
                  data-testid={TESTIDS.cities.card(c.slug)}
                  className="group flex items-center justify-between border border-white/10 hover:border-amber-500 hover:bg-amber-500 px-5 py-4 transition-colors duration-300"
                >
                  <span className="font-display uppercase tracking-tight text-white group-hover:text-black text-lg font-bold">{c.name}</span>
                  <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-black transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              ))}
            </div>
          </div>
        ))}
      </section>
    </>
  );
};

// --- SEO content templates for city detail ---

const cityFaqs = (c, lang) => {
  const en = [
    {
      q: `Are you a licensed security guard company in ${c.name}?`,
      a: `Yes. Quebec Security Services is a Bureau de la sécurité privée (BSP) permitted security firm operating throughout Quebec, including full deployment coverage in ${c.name} and the surrounding ${c.region} region. Every officer on our ${c.name} roster carries an active BSP license, verifiable on demand through our client portal.`,
    },
    {
      q: `How quickly can you deploy security guards in ${c.name}?`,
      a: `Standard security guard deployments in ${c.name} launch within 48–72 hours of contract signature. Emergency deployments — post-incident fire watch, workplace-violence standby, immediate mobile patrol coverage — are available same-day from our rapid-response benches staged in the ${c.region} region and neighbouring Quebec urban centres.`,
    },
    {
      q: `What security services do you offer in ${c.name}?`,
      a: `We deliver the full spectrum of security services in ${c.name}: static uniformed guards, mobile patrol, event security, executive protection, construction site security, retail loss prevention, corporate concierge, residential and estate protection, alarm response, K9 detection, fire watch, cash-in-transit, and specialized industry-specific coverage. Every discipline runs under one BSP-licensed roof for single-source accountability.`,
    },
    {
      q: `Do your ${c.name} officers speak French and English?`,
      a: `Every officer deployed in ${c.name} is bilingual to CEFR B2 minimum in both French and English. Tenant-facing posts — concierge, corporate reception, hotel front-of-house, hospitality — are held to CEFR C1 conversational standard. Your Quebec workforce, tenants, and guests are served in the language they prefer.`,
    },
    {
      q: `What is your alarm response time in ${c.name}?`,
      a: `Median verified alarm response in ${c.name} is under 12 minutes from dispatch. Every alarm response is dispatched from our own Quebec Command Centre — not a third-party monitoring station — with a documented chain-of-command relationship to the responding officer and a time-stamped digital incident report generated for every event.`,
    },
    {
      q: `Can you provide security for events in ${c.name}?`,
      a: `Yes. Our event security division services concerts, festivals, corporate galas, weddings, private parties, sporting events, and trade shows across ${c.name}. Deployments range from a two-officer boutique event to full 200+ officer festival operations with crowd management, magnetometer screening, K9 explosive detection, and VIP escort.`,
    },
    {
      q: `Do you provide insurance certificates for ${c.name} contracts?`,
      a: `Yes. Quebec Security Services carries $10M commercial general liability, professional liability, and CNESST workers' compensation coverage. Certificates of insurance are issued at contract execution for ${c.name} engagements and automatically renewed at policy anniversary. Additional-insured endorsement is available on request for landlords, GCs, and event producers.`,
    },
  ];
  const fr = [
    {
      q: `Êtes-vous une entreprise de sécurité licenciée à ${c.name}?`,
      a: `Oui. Quebec Security Services est titulaire d'un permis d'agence du Bureau de la sécurité privée (BSP) opérant partout au Québec, incluant une couverture complète à ${c.name} et dans la région ${c.region}.`,
    },
    {
      q: `Quelle est votre rapidité de déploiement à ${c.name}?`,
      a: `Les déploiements standards à ${c.name} se lancent en 48-72 heures. Les déploiements d'urgence sont disponibles le jour même depuis nos équipes de réponse rapide.`,
    },
    {
      q: `Quels services de sécurité offrez-vous à ${c.name}?`,
      a: `Nous offrons l'éventail complet à ${c.name} : agents statiques, patrouille mobile, événements, protection rapprochée, chantiers, prévention des pertes, concierge corporatif, résidentiel, intervention sur alarme, K9, surveillance incendie, transport de valeurs.`,
    },
    {
      q: `Vos agents à ${c.name} parlent-ils français et anglais?`,
      a: `Chaque agent déployé à ${c.name} est bilingue au minimum au niveau CECR B2 en français et anglais.`,
    },
  ];
  return lang === "fr" ? fr : en;
};

const cityThreats = (c, lang) => {
  const isMining = c.industries?.some((i) => /mining|iron|gold|lithium|nickel|copper/i.test(i));
  const isPort = c.industries?.some((i) => /port|shipping|marine/i.test(i));
  const isTourism = c.industries?.some((i) => /tourism|hospitality/i.test(i));
  const isCorporate = /Montreal|Quebec|Laval|Gatineau|Sherbrooke/i.test(c.name);
  if (lang === "fr") {
    const items = [];
    items.push({
      title: "Vol commercial et au détail",
      body: `Les commerces de ${c.name} font face au crime organisé au détail, qui cible les produits à forte marge. Notre division de prévention des pertes réduit le vol de 34 % en moyenne en 90 jours.`,
    });
    items.push({
      title: "Vol sur les chantiers",
      body: `Le vol de cuivre, d'outils et d'équipement coûte des millions chaque année à l'industrie de la construction de la région ${c.region}. La patrouille mobile et les rondes K9 réduisent les pertes à ${c.name}.`,
    });
    if (isMining) {
      items.push({
        title: "Périmètre minier et vol de minerai",
        body: `${c.name} se situe dans un corridor minier actif. L'interception de transport de minerai et les intrusions de périmètre exigent un déploiement spécialisé.`,
      });
    }
    if (isPort) {
      items.push({
        title: "Sécurité portuaire et des cargaisons",
        body: `Les infrastructures portuaires de ${c.name} exigent un contrôle de périmètre conforme au code ISPS et une surveillance des zones restreintes.`,
      });
    }
    if (isTourism) {
      items.push({
        title: "Risque hôtelier et événementiel",
        body: `Le tourisme à ${c.name} alimente la demande en sécurité événementielle et hôtelière — de la réponse nocturne à la coordination d'arrivées VIP.`,
      });
    }
    if (isCorporate) {
      items.push({
        title: "Prévention de la violence au travail",
        body: `La densité corporative de ${c.name} alimente la demande — congédiements à risque, ordonnances judiciaires et gestion des menaces.`,
      });
    }
    items.push({
      title: "Protection résidentielle",
      body: `Résidences de luxe, tours de condos et communautés à accès contrôlé de ${c.name} comptent sur notre conciergerie, notre patrouille mobile et notre intervention sur alarme.`,
    });
    return items.slice(0, 6);
  }
  const items = [];
  items.push({
    title: "Retail & commercial theft",
    body: `${c.name} businesses face organized retail crime (ORC) crews rotating on 14-day cycles, targeting high-margin SKUs. Our LP division reduces shrink by an average of 34% within 90 days.`,
  });
  items.push({
    title: "Construction site theft",
    body: `Copper, tools, and equipment theft cost the ${c.region} construction industry millions annually. Mobile patrol with LPR cameras and K9 sweeps measurably reduce jobsite losses in ${c.name}.`,
  });
  if (isMining) {
    items.push({
      title: "Mining perimeter & ore theft",
      body: `${c.name} sits in an active mining corridor. Ore-transport interception, camp perimeter breaches, and after-hours access to decommissioned adits require specialized deployment.`,
    });
  }
  if (isPort) {
    items.push({
      title: "Port & cargo security",
      body: `${c.name}'s port and shipping infrastructure demands ISPS Code-aligned perimeter control, container-yard patrol, and restricted-area monitoring.`,
    });
  }
  if (isTourism) {
    items.push({
      title: "Hospitality & event risk",
      body: `${c.name}'s tourism traffic drives event, festival, and hotel security demand — from overnight guest response to VIP arrival coordination.`,
    });
  }
  if (isCorporate) {
    items.push({
      title: "Workplace violence prevention",
      body: `${c.name}'s dense corporate footprint drives WVP demand — high-risk terminations, court-order enforcement, and executive-floor threat management.`,
    });
  }
  items.push({
    title: "Residential & estate protection",
    body: `Luxury residences, condominium towers, and gated communities in ${c.name} rely on our concierge, mobile patrol, and alarm-response layer.`,
  });
  return items.slice(0, 6);
};

const cityWhy = (c, lang) =>
  lang === "fr"
    ? [
        { title: "Équipe locale", body: `Nos agents à ${c.name} connaissent chaque code postal, chaque secteur commercial, chaque exigence locale.` },
        { title: "Licenciés BSP", body: "Chaque agent déployé détient une licence active du Bureau de la sécurité privée." },
        { title: "Bilingues FR/EN", body: "CECR B2 minimum. Vos locataires, employés et invités servis dans leur langue préférée." },
        { title: "Centre de commandement 24/7", body: "Répartition depuis notre centre à Montréal. Réponse médiane vérifiée sous 12 minutes." },
      ]
    : [
        { title: "Local knowledge", body: `Our ${c.name} roster knows every postal code, every commercial district, every local operating requirement.` },
        { title: "BSP-licensed", body: "Every officer deployed carries an active Bureau de la sécurité privée license." },
        { title: "Bilingual FR/EN", body: "CEFR B2 minimum. Your tenants, employees, and guests served in the language they prefer." },
        { title: "24/7 Command Centre", body: "Dispatch from our own Montreal command centre. Median verified response under 12 minutes." },
      ];

// ---- City Detail ----

export const CityDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const c = getCityBySlug(slug);
  if (!c) return <Navigate to="/cities" replace />;
  // Only the fully translated cities live under /fr — bounce the rest to EN
  if (lang === "fr" && !FR_CITY_SLUGS.includes(c.slug)) return <Navigate to={`/cities/${c.slug}`} replace />;

  const lp = (p) => localizedPath(p, lang);
  const allServices = services; // now show all 24 services on every city page for SEO depth
  const otherCities = cities.filter((x) => x.slug !== c.slug).slice(0, 20);
  const faqs = cityFaqs(c, lang);
  const threats = cityThreats(c, lang);
  const why = cityWhy(c, lang);

  const intro = lang === "fr"
    ? `${c.name} est desservie 24/7 par Quebec Security Services. De la patrouille mobile aux agents statiques licenciés BSP, notre couverture de ${c.name} inclut chaque grand secteur — corporatif, industriel, résidentiel, événementiel et minier lorsque applicable. Nos équipes opèrent depuis des points stratégiques dans la région ${c.region}, garantissant un déploiement rapide.`
    : `${c.name} is served 24/7 by Quebec Security Services. From mobile patrol to BSP-licensed static guards, our coverage of ${c.name} includes every major sector — corporate, industrial, residential, event, and mining where applicable. Our teams operate from strategic staging points across the ${c.region} region, guaranteeing rapid deployment.`;

  const localBody1 = (lang === "fr" ? c.local_fr : c.local_en) ||
    `Since 2011, Quebec Security Services has protected businesses, residences, construction sites, and critical infrastructure across ${c.name}. Our local team knows every postal code, every commercial district, and every regulatory requirement specific to ${c.region}. Whether you need a static concierge for a building near ${c.landmarks || c.name}, a mobile patrol swing for a parking structure, or a full-perimeter deployment for a construction site, our BSP-licensed officers are trained specifically for the ${c.name} operating environment.`;

  const localBody2 = lang === "fr"
    ? `Chaque déploiement à ${c.name} est planifié à partir d'une visite réelle des lieux. Nos consultants cartographient vos vulnérabilités, documentent les exigences de la CNESST et alignent l'opération sur les normes du Bureau de la sécurité privée (BSP) et la Loi 25. Le résultat : une opération conçue pour votre bâtiment, votre entreprise et votre rue — jamais un modèle copié-collé.`
    : `Every ${c.name} deployment is scoped from a physical site survey. Our security consultants map your specific vulnerabilities, document CNESST workplace-safety requirements, and align the operation with Bureau de la sécurité privée (BSP) licensing standards and Loi 25 personal-information handling. The result is a security operation designed for your building, your business, and the specific street where you operate — not a copy-paste template.`;

  const localBody3 = lang === "fr"
    ? `Les clients de ${c.name} ont accès à notre centre de commandement québécois 24/7. Chaque incident est consigné, chaque quart est documenté dans un rapport quotidien, et chaque obligation réglementaire est accompagnée d'une documentation de calibre assurance, accessible en temps réel via votre portail client.`
    : `${c.name} clients gain access to our 24/7 Quebec Command Centre with median verified alarm response under 12 minutes across the ${c.region} region. Every incident is logged, every shift is documented in a daily activity report (DAR), and every regulatory obligation is met with insurance-grade paperwork available in real time through your client portal.`;

  return (
    <>
      <Helmet>
        <title>{lang === "fr" ? c.seo_title_fr || c.seo_title : c.seo_title}</title>
        <meta name="description" content={lang === "fr" ? c.meta_description_fr || c.meta_description : c.meta_description} />
        <meta name="keywords" content={[...c.keywords, ...sharedCanadianKeywords].join(", ")} />
        <link rel="canonical" href={`${SITE_BASE_URL}${lang === "fr" ? `/fr/cities/${c.slug}` : `/cities/${c.slug}`}`} />
        {(hreflangLinks(`/cities/${c.slug}`) || []).map((l) => (
          <link key={l.hrefLang} rel={l.rel} hrefLang={l.hrefLang} href={l.href} />
        ))}
      </Helmet>
      <JsonLd data={citySchema(c)} />
      <JsonLd data={breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Cities", path: "/cities" },
        { name: c.name, path: `/cities/${c.slug}` },
      ])} />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end bg-black overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1519178614-68673b201f36?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
          alt={`${c.name} skyline`}
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 md:px-10 py-20 md:py-24">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 flex items-center gap-3 mb-8">
            <span className="w-8 h-px bg-amber-500" />
            {c.region}
          </div>
          <div className="text-2xl md:text-3xl text-zinc-300 font-display uppercase tracking-tight mb-4">
            {t("cities.detail_intro")}
          </div>
          <h1
            data-testid={TESTIDS.cities.detailTitle}
            className="font-display uppercase font-black text-6xl md:text-9xl lg:text-[12vw] tracking-tight leading-[0.85] text-white"
          >
            <MaskedReveal><span>{c.name}</span></MaskedReveal>
          </h1>
          <FadeIn delay={0.2}>
            <div className="mt-6 flex flex-wrap gap-6 text-sm font-mono uppercase tracking-[0.15em] text-zinc-400">
              <div><span className="text-amber-500">◉</span> {c.population}</div>
              {c.nickname && <div><span className="text-amber-500">◉</span> {c.nickname}</div>}
              <div><span className="text-amber-500">◉</span> {t("common.response_247")}</div>
              <div><span className="text-amber-500">◉</span> {t("common.licensed")}</div>
              <div><span className="text-amber-500">◉</span> {t("common.bilingual")}</div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Overview + sidebar */}
      <section className="px-6 md:px-10 py-24 max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-8">
          <FadeIn>
            <p className="text-xl md:text-2xl text-white leading-relaxed">{intro}</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p className="mt-8 text-base md:text-lg text-zinc-400 leading-relaxed">{localBody1}</p>
            <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed">{localBody2}</p>
            <p className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed">{localBody3}</p>
          </FadeIn>

          {c.landmarks && (
            <FadeIn delay={0.15}>
              <div className="mt-14 border-t border-white/10 pt-10">
                <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-4">→ {lang === "fr" ? `Zones couvertes à ${c.name}` : `${c.name} coverage zones`}</div>
                <p className="text-zinc-300 text-lg leading-relaxed">{c.landmarks}</p>
              </div>
            </FadeIn>
          )}

          {c.industries?.length > 0 && (
            <FadeIn delay={0.2}>
              <div className="mt-10">
                <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">→ {lang === "fr" ? `Industries desservies à ${c.name}` : `Industries served in ${c.name}`}</div>
                <div className="flex flex-wrap gap-2">
                  {c.industries.map((ind) => (
                    <span key={ind} className="border border-white/15 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.15em] text-zinc-400">
                      {ind}
                    </span>
                  ))}
                </div>
              </div>
            </FadeIn>
          )}
        </div>

        <aside className="md:col-span-4 md:pl-8 md:border-l md:border-white/10">
          <FadeIn>
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-4">→ {lang === "fr" ? `Déployer à ${c.name}` : `Deploy in ${c.name}`}</div>
            <h3 className="font-display uppercase font-black text-3xl md:text-4xl tracking-tight leading-none text-white">
              {lang === "fr" ? (<>Soumission<br />locale.</>) : (<>Get a<br />local quote.</>)}
            </h3>
            <p className="mt-4 text-zinc-400 text-sm">{lang === "fr" ? `Un consultant basé à ${c.name} répond en un jour ouvrable.` : `A ${c.name}-based consultant will reply in one business day.`}</p>
            <Link
              to="/contact"
              data-testid={TESTIDS.cities.detailCta}
              className="mt-6 group inline-flex items-center justify-between gap-4 bg-amber-500 hover:bg-amber-400 text-black h-12 px-5 w-full font-mono text-[11px] uppercase tracking-[0.2em] transition-colors"
            >
              {t("common.request_quote")}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="mt-12 border-t border-white/10 pt-8 space-y-4">
              <SidebarStat icon={MapPin} label={lang === "fr" ? "Région" : "Region"} value={c.region} />
              <SidebarStat icon={Clock} label={lang === "fr" ? "Réponse alarme" : "Alarm Response"} value="< 12 min" />
              <SidebarStat icon={Shield} label="Licence" value="BSP · Category A/C" />
            </div>
          </FadeIn>
        </aside>
      </section>

      {/* Why us in this city */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ {lang === "fr" ? `Pourquoi nous à ${c.name}` : `Why us in ${c.name}`}</div>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h2 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9] text-white max-w-4xl">
            {lang === "fr"
              ? `Services d'agents de sécurité à ${c.name} — la norme opérationnelle.`
              : `Security guard services in ${c.name} — the operating standard.`}
          </h2>
        </FadeIn>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {why.map((w, i) => (
            <FadeIn key={w.title} delay={i * 0.04}>
              <div className="border border-white/10 hover:border-amber-500 transition-colors p-8 h-full">
                <div className="w-10 h-10 bg-amber-500 text-black flex items-center justify-center mb-6">
                  <Check className="w-5 h-5" />
                </div>
                <h3 className="font-display uppercase text-xl tracking-tight text-white">{w.title}</h3>
                <p className="mt-3 text-sm text-zinc-400">{w.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* Threat profile / typical needs */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ {lang === "fr" ? "Profil de menace" : "Threat profile"}</div>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h2 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9] text-white max-w-4xl">
            {lang === "fr" ? `Ce que nous protégeons à ${c.name}.` : `What we secure in ${c.name}.`}
          </h2>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="mt-6 text-zinc-400 text-lg max-w-3xl">
            {lang === "fr"
              ? `Chaque déploiement à ${c.name} commence par un profil de menace — les risques précis observés par notre équipe dans la région ${c.region}.`
              : `Every ${c.name} deployment starts with a threat profile — the specific risk patterns our team has observed operating in the ${c.region} region.`}
          </p>
        </FadeIn>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {threats.map((th, i) => (
            <FadeIn key={th.title} delay={i * 0.04}>
              <div className="border border-white/10 hover:border-amber-500 transition-colors p-8 h-full">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-4">/ {String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display uppercase text-xl tracking-tight text-white">{th.title}</h3>
                <p className="mt-3 text-sm text-zinc-400">{th.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* All services deployed in this city */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ {lang === "fr" ? `${allServices.length} services à ${c.name}` : `${allServices.length} services in ${c.name}`}</div>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h2 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9] text-white max-w-4xl">
            {lang === "fr" ? (<>Chaque service de sécurité.<br />Déployé à {c.name}.</>) : (<>Every security service.<br />Deployed in {c.name}.</>)}
          </h2>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="mt-6 text-zinc-400 text-lg max-w-3xl">
            {lang === "fr"
              ? `L'éventail complet des opérations licenciées BSP est disponible à ${c.name} et dans la région ${c.region} — d'un agent seul aux opérations de festival de 200+ agents.`
              : `The full spectrum of BSP-licensed security operations is available for deployment in ${c.name} and the surrounding ${c.region} region — single-officer coverage through 200+ officer festival operations.`}
          </p>
        </FadeIn>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {allServices.map((s) => (
            <Link
              key={s.slug}
              to={lp(`/services/${s.slug}`)}
              className="group flex items-start justify-between border border-white/10 hover:border-amber-500 p-5 transition-colors"
            >
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 group-hover:text-amber-500">/ {s.number}</div>
                <div className="font-display uppercase text-base md:text-lg tracking-tight text-white group-hover:text-amber-500 mt-1 leading-tight">
                  {lang === "fr" ? s.name_fr : s.name_en}
                </div>
                <div className="mt-1 text-xs text-zinc-500">
                  {(lang === "fr" ? s.tagline_fr : s.tagline_en)}
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-amber-500 shrink-0" />
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <FadeIn>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ FAQ · {c.name}</div>
        </FadeIn>
        <FadeIn delay={0.05}>
          <h2 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9] text-white max-w-4xl">
            {lang === "fr"
              ? `Services d'agents de sécurité à ${c.name} — vos questions, nos réponses.`
              : `Security guard services in ${c.name} — questions answered.`}
          </h2>
        </FadeIn>
        <div className="mt-14 max-w-4xl">
          {faqs.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} defaultOpen={i === 0} />)}
        </div>
        <JsonLd data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }} />
      </section>

      {/* SEO keyword grid */}
      <section className="border-t border-white/10 px-6 md:px-10 py-14 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">→ {lang === "fr" ? `Termes de recherche couverts à ${c.name} · chaque mot-clé mène à sa propre page` : `Search terms we cover in ${c.name} · every keyword links to its own page`}</div>
        <div className="flex flex-wrap gap-2">
          {[...new Set([
            ...c.keywords,
            `security guard ${c.name}`,
            `security services ${c.name}`,
            `mobile patrol ${c.name}`,
            `event security ${c.name}`,
            `construction security ${c.name}`,
            `corporate security ${c.name}`,
            `residential security ${c.name}`,
            `mining security ${c.name}`,
            `24/7 security ${c.name}`,
            `bilingual security ${c.name}`,
            `BSP licensed security ${c.name}`,
            `agent de sécurité ${c.name}`,
            `security company near me ${c.name}`,
            `hire security ${c.name}`,
            `emergency security ${c.name}`,
            `${c.name} security`,
          ])].map((k) => (
            <Link
              key={k}
              to={lp(`/seo/${slugifyKeyword(k)}`)}
              className="group border border-white/10 hover:border-amber-500 hover:bg-amber-500 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 hover:text-black transition-colors inline-flex items-center gap-1.5"
            >
              {k}
              <ArrowUpRight className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
        </div>

        <div className="mt-10 pt-8 border-t border-white/10">
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-zinc-500 mb-4">→ {lang === "fr" ? `Industrie canadienne de la sécurité — termes de recherche · ${c.name}` : `Canadian security industry — ${c.name} search terms`}</div>
          <div className="flex flex-wrap gap-2">
            {topCanadianKeywords.map((k) => (
              <Link
                key={`can-${k}`}
                to={lp(`/seo/${slugifyKeyword(k)}`)}
                className="group border border-white/10 hover:border-amber-500 hover:bg-amber-500 px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 hover:text-black transition-colors inline-flex items-center gap-1.5"
              >
                {k}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby cities */}
      <section className="border-t border-white/10 px-6 md:px-10 py-24 max-w-[1600px] mx-auto">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-amber-500 mb-6">→ {lang === "fr" ? "Villes québécoises à proximité" : "Nearby Quebec cities"}</div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {otherCities.map((oc) => (
            <Link key={oc.slug} to={lp(`/cities/${oc.slug}`)} className="group flex items-center justify-between border border-white/10 hover:border-amber-500 hover:bg-amber-500 px-4 py-3 transition-colors">
              <span className="font-display uppercase text-base tracking-tight text-white group-hover:text-black">{oc.name}</span>
              <ArrowUpRight className="w-3 h-3 text-zinc-600 group-hover:text-black" />
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-amber-500 text-black">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-20">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.3em] mb-4">→ {c.name}</div>
              <h3 className="font-display uppercase font-black text-4xl md:text-6xl tracking-tight leading-[0.9]">
                {lang === "fr" ? (<>Déployez à {c.name}.<br />Parlez-nous.</>) : (<>Deploy in {c.name}.<br />Talk to us.</>)}
              </h3>
            </div>
            <Link to="/contact" className="group inline-flex items-center gap-4 bg-black text-amber-500 h-14 px-6 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-zinc-900 transition-colors self-start">
              {t("common.request_quote")}
              <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

const SidebarStat = ({ icon: Icon, label, value }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500 mb-1 flex items-center gap-2">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className="text-white">{value}</div>
  </div>
);

const FaqItem = ({ q, a, defaultOpen = false }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="border-b border-white/10">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between gap-6 py-6 text-left group">
        <span className="font-display uppercase text-lg md:text-2xl tracking-tight text-white group-hover:text-amber-500 transition-colors">{q}</span>
        <ChevronDown className={`w-5 h-5 text-amber-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
        className="overflow-hidden"
      >
        <p className="pb-6 pr-10 text-zinc-400 text-base leading-relaxed">{a}</p>
      </motion.div>
    </div>
  );
};
